<template>
  <div class="dashboard-modal-card">
    <!-- Card déclencheuse. Un vrai <button> : un <div @click> régresserait
         l'accessibilité. Elle porte `card` et son apparence vient du socle importé
         plus bas — elle ne le recopie plus, donc elle ne peut plus en dériver. -->
    <button ref="trigger" type="button" class="card modal-card" :aria-haspopup="'dialog'" @click="openModal">
      <span class="card-header">
        <span class="card-title">{{ title }}</span>
        <span v-if="badgeLabel" class="badge" :class="badgeClass">{{ badgeLabel }}</span>
      </span>
      <span class="card-description">{{ description }}</span>
      <span class="card-link">{{ linkLabel }} →</span>
    </button>

    <!-- Fenêtre modale -->
    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
      <div ref="dialog" class="modal-dialog" role="dialog" aria-modal="true" tabindex="-1" :aria-labelledby="titleId">
        <div class="modal-header">
          <h2 :id="titleId" class="modal-title">{{ modalTitle || title }}</h2>
          <button
            ref="closeButton"
            type="button"
            class="modal-close"
            aria-label="Fermer la fenêtre"
            @click="closeModal"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div class="modal-content">
          <!-- Le contenu de la destination, extrait en composant et monté ici.
               `isOpen` est passé au slot pour permettre un chargement paresseux. -->
          <slot :is-open="isOpen" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ")

let cardUid = 0

/**
 * Card ouvrant son contenu en fenêtre modale, sans quitter le dashboard hôte.
 *
 * Factorise le motif card+modale que les dashboards recopiaient à l'identique
 * (`.cards-grid` / `.card` / `.modal-overlay`). Le contenu de la destination est
 * fourni par le slot par défaut : la card ne connaît pas ce qu'elle affiche.
 *
 * RGAA : le focus est piégé dans la modale tant qu'elle est ouverte, elle se ferme
 * au clavier (Échap), et le focus revient sur la card déclencheuse à la fermeture.
 * Logique reprise de `MealFeedbackPanel`, seule implémentation modale accessible en
 * place — à faire évoluer ici, pas à recopier.
 */
export default {
  name: "DashboardModalCard",

  props: {
    title: {
      type: String,
      required: true,
    },
    // Titre de la fenêtre si différent du titre de la card ; sinon on reprend `title`.
    modalTitle: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    linkLabel: {
      type: String,
      default: "Ouvrir",
    },
    // État rendu par un texte, jamais par la seule couleur (RGAA).
    badgeLabel: {
      type: String,
      default: "",
    },
    // Une des variantes de badge du socle : blue / green / orange / yellow / red / purple / grey.
    badgeVariant: {
      type: String,
      default: "blue",
    },
  },

  data() {
    cardUid += 1
    return {
      cardId: cardUid,
      isOpen: false,
      keyboardTrapAttached: false,
    }
  },

  computed: {
    titleId() {
      return `dashboard-modal-title-${this.cardId}`
    },

    badgeClass() {
      return `badge-${this.badgeVariant}`
    },
  },

  beforeDestroy() {
    this.releaseBodyScroll()
    this.detachKeyboardTrap()
  },

  methods: {
    openModal() {
      this.isOpen = true
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = "hidden"
      }
      this.attachKeyboardTrap()
      this.$emit("open")
      this.$nextTick(() => this.focusFirstElement())
    },

    closeModal() {
      if (!this.isOpen) return
      this.isOpen = false
      this.detachKeyboardTrap()
      this.releaseBodyScroll()
      this.$emit("close")
      // RGAA : le focus revient sur l'élément déclencheur.
      this.$nextTick(() => {
        if (this.$refs.trigger && this.$refs.trigger.focus) this.$refs.trigger.focus()
      })
    },

    releaseBodyScroll() {
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = ""
      }
    },

    /**
     * Le clavier est écouté au niveau du document, et non sur le dialogue : un contenu
     * de destination peut détruire l'élément focalisé (bascule formulaire → confirmation),
     * et le focus retomberait alors sur `document.body`, hors du dialogue.
     */
    attachKeyboardTrap() {
      if (typeof document === "undefined" || this.keyboardTrapAttached) return
      document.addEventListener("keydown", this.onDocumentKeydown, true)
      this.keyboardTrapAttached = true
    },

    detachKeyboardTrap() {
      if (typeof document === "undefined" || !this.keyboardTrapAttached) return
      document.removeEventListener("keydown", this.onDocumentKeydown, true)
      this.keyboardTrapAttached = false
    },

    onDocumentKeydown(event) {
      if (!this.isOpen) return
      const key = event.key
      if (key === "Escape" || key === "Esc" || event.keyCode === 27) {
        event.preventDefault()
        event.stopPropagation()
        this.closeModal()
      } else if (key === "Tab" || event.keyCode === 9) {
        this.onTabKey(event)
      }
    },

    focusableElements() {
      const dialog = this.$refs.dialog
      if (!dialog || !dialog.querySelectorAll) return []
      return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true"
      )
    },

    focusFirstElement() {
      const focusable = this.focusableElements()
      const target = focusable.length ? focusable[0] : this.$refs.dialog
      if (target && target.focus) target.focus()
    },

    /**
     * Piège le focus : Tab depuis le dernier élément revient au premier, et
     * Maj+Tab depuis le premier repart sur le dernier.
     */
    onTabKey(event) {
      const focusable = this.focusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const dialog = this.$refs.dialog
      const active = typeof document !== "undefined" ? document.activeElement : null
      const activeIsOutside = !active || !dialog || !(dialog === active || dialog.contains(active))

      if (event.shiftKey && (active === first || activeIsOutside)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || activeIsOutside)) {
        event.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style scoped lang="scss">
// Socle partagé des cards et des badges — source unique. Les valeurs recopiées ici
// avaient dérivé sur trois badges (orange, rouge, jaune) : l'import les réaligne.
@import "../scss/dashboard-card";

/* Le composant est posé dans la `.cards-grid` du dashboard : sa card doit prendre
   toute la hauteur de la cellule pour s'aligner sur ses voisines. */
.dashboard-modal-card,
.modal-card {
  height: 100%;
}

/* Les enfants de la card sont des `span` — un bouton ne peut pas contenir de bloc —
   donc leur disposition est rétablie ici, comme dans `DashboardCard`. */
.card-description {
  display: block;
}

.card-header {
  gap: 0.5rem;
  width: 100%;
}

.modal-card:hover .card-link {
  color: #1d4ed8;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 2000;
}

.modal-dialog {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.modal-close {
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  width: 2.25rem;
  height: 2.25rem;
  font-size: 1rem;
  cursor: pointer;
  color: #1a1a1a;
  flex-shrink: 0;
}

.modal-close:hover {
  background: #e5e7eb;
}

.modal-close:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}
</style>
